import { useAuth } from "@/_core/hooks/useAuth";
import { SitePageShell } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { getLoginUrl } from "@/const";
import { openDaumPostcode } from "@/lib/daumPostcode";
import { trpc } from "@/lib/trpc";
import { formatKRW } from "@shared/pricing";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { StepIndicator } from "./Create";

declare global {
  interface Window {
    TossPayments?: any;
  }
}

export default function CheckoutPage() {
  const { isAuthenticated, loading, user } = useAuth();
  const [, navigate] = useLocation();
  const params = useMemo(() => {
    const url = new URL(window.location.href);
    return {
      imageId: Number(url.searchParams.get("image") ?? "0"),
      sizeId: Number(url.searchParams.get("size") ?? "0"),
      frameId: Number(url.searchParams.get("frame") ?? "0"),
      finishId: Number(url.searchParams.get("finish") ?? "0"),
    };
  }, []);

  const productIds = useMemo(
    () =>
      [params.sizeId, params.frameId, params.finishId].filter((v) => v > 0),
    [params.sizeId, params.frameId, params.finishId],
  );

  const estimate = trpc.orders.estimate.useQuery(
    { productIds },
    { enabled: productIds.length > 0 },
  );

  const [recipient, setRecipient] = useState({
    name: "",
    phone: "",
    postalCode: "",
    address1: "",
    address2: "",
    note: "",
  });

  const createOrder = trpc.orders.create.useMutation();
  const confirmPayment = trpc.payments.confirm.useMutation();
  const [isPending, setIsPending] = useState(false);
  const tossLoadedRef = useRef(false);

  const handleAddressSearch = () => {
    openDaumPostcode()
      .then(({ postalCode, address }) => {
        setRecipient((prev) => ({ ...prev, postalCode, address1: address }));
        document.getElementById("checkout-address2")?.focus();
      })
      .catch(() => toast.error("주소 검색을 불러오지 못했습니다."));
  };

  // 토스페이먼츠 SDK 로드
  useEffect(() => {
    if (tossLoadedRef.current) return;
    const script = document.createElement("script");
    script.src = "https://js.tosspayments.com/v2/standard";
    script.async = true;
    script.onload = () => {
      tossLoadedRef.current = true;
    };
    document.head.appendChild(script);
    return () => {
      if (script.parentNode === document.head) {
        document.head.removeChild(script);
      }
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      window.location.href = getLoginUrl();
    }
    if (!params.imageId || !params.sizeId) {
      navigate("/create");
    }
  }, [loading, isAuthenticated, params, navigate]);

  // 토스 결제 성공 콜백 처리 (리다이렉트 후)
  useEffect(() => {
    const url = new URL(window.location.href);
    const paymentKey = url.searchParams.get("paymentKey");
    const tossOrderId = url.searchParams.get("orderId");
    const amount = url.searchParams.get("amount");
    const dbOrderId = url.searchParams.get("dbOrderId");

    if (paymentKey && tossOrderId && amount && dbOrderId) {
      // 결제 승인 처리
      setIsPending(true);
      confirmPayment
        .mutateAsync({
          orderId: Number(dbOrderId),
          paymentKey,
          amount: Number(amount),
        })
        .then((result) => {
          if (result.success) {
            navigate(`/order-complete?orderId=${result.orderId}`);
          }
        })
        .catch((err) => {
          toast.error(err.message || "결제 승인에 실패했습니다.");
          setIsPending(false);
        });
    }
  }, []);

  if (loading || !isAuthenticated) {
    return (
      <SitePageShell>
        <div className="container py-20 flex justify-center">
          <Loader2 className="animate-spin text-muted-foreground" />
        </div>
      </SitePageShell>
    );
  }

  const submit = async () => {
    if (
      !recipient.name ||
      !recipient.phone ||
      !recipient.postalCode ||
      !recipient.address1
    ) {
      toast.error("배송정보를 모두 입력해주세요.");
      return;
    }

    setIsPending(true);

    try {
      // 1. 주문 생성
      const order = await createOrder.mutateAsync({
        generatedImageId: params.imageId,
        productIds,
        recipient: {
          name: recipient.name,
          phone: recipient.phone,
          postalCode: recipient.postalCode,
          address1: recipient.address1,
          address2: recipient.address2 || undefined,
          note: recipient.note || undefined,
        },
        paymentProvider: "toss",
      });

      // 2. 토스페이먼츠 결제 위젯 실행
      const clientKey = import.meta.env.VITE_TOSS_CLIENT_KEY || "test_ck_DnyRpQWGrNzgM2aqJ0ogrKwv1M9E";

      if (!window.TossPayments) {
        toast.error("결제 모듈을 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
        setIsPending(false);
        return;
      }

      const tossPayments = window.TossPayments(clientKey);
      const payment = tossPayments.payment({ customerKey: `user_${user?.id ?? "guest"}` });

      const successUrl = new URL(window.location.href);
      successUrl.searchParams.set("dbOrderId", String(order.orderId));

      const failUrl = new URL(window.location.href);
      failUrl.searchParams.set("paymentFailed", "true");

      await payment.requestPayment({
        method: "CARD",
        amount: {
          currency: "KRW",
          value: order.amount,
        },
        orderId: order.orderNo,
        orderName: "유화 캔버스 작품",
        customerName: user?.name || recipient.name,
        customerEmail: user?.email || undefined,
        successUrl: successUrl.toString(),
        failUrl: failUrl.toString(),
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "주문에 실패했습니다.";
      if (message !== "사용자가 결제를 취소했습니다" && message !== "USER_CANCEL") {
        toast.error(message);
      }
      setIsPending(false);
    }
  };

  // 결제 실패 파라미터 처리
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("paymentFailed") === "true") {
      const code = url.searchParams.get("code") ?? "";
      const msg = url.searchParams.get("message") ?? "결제에 실패했습니다.";
      if (code !== "PAY_PROCESS_CANCELED") {
        toast.error(msg);
      }
      // URL 정리
      url.searchParams.delete("paymentFailed");
      url.searchParams.delete("code");
      url.searchParams.delete("message");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  return (
    <SitePageShell>
      <div className="container py-10 md:py-14">
        <StepIndicator current={3} />

        <div className="mt-10 grid lg:grid-cols-12 gap-8">
          <div className="order-2 lg:order-1 lg:col-span-7 space-y-6">
            <div>
              <h1 className="font-serif text-3xl text-primary">
                배송정보 및 결제
              </h1>
              <p className="text-muted-foreground text-sm mt-2">
                작품을 받으실 곳을 알려주시면 안전하게 배송해 드립니다.
              </p>
            </div>

            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="font-serif text-lg text-primary">
                  받는 분 정보
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="이름">
                    <Input
                      value={recipient.name}
                      onChange={(e) =>
                        setRecipient({ ...recipient, name: e.target.value })
                      }
                      placeholder="홍길동"
                    />
                  </Field>
                  <Field label="연락처">
                    <Input
                      value={recipient.phone}
                      onChange={(e) =>
                        setRecipient({ ...recipient, phone: e.target.value })
                      }
                      placeholder="010-0000-0000"
                    />
                  </Field>
                </div>
                <Field label="우편번호">
                  <div className="flex gap-2">
                    <Input
                      value={recipient.postalCode}
                      readOnly
                      placeholder="주소 검색을 눌러주세요"
                      className="max-w-40"
                    />
                    <Button type="button" variant="outline" onClick={handleAddressSearch}>
                      주소 검색
                    </Button>
                  </div>
                </Field>
                <Field label="기본주소">
                  <Input
                    value={recipient.address1}
                    readOnly
                    placeholder="주소 검색을 눌러주세요"
                  />
                </Field>
                <Field label="상세 주소">
                  <Input
                    id="checkout-address2"
                    value={recipient.address2}
                    onChange={(e) =>
                      setRecipient({ ...recipient, address2: e.target.value })
                    }
                    placeholder="101동 1503호"
                  />
                </Field>
                <Field label="요청사항">
                  <Textarea
                    rows={3}
                    value={recipient.note}
                    onChange={(e) =>
                      setRecipient({ ...recipient, note: e.target.value })
                    }
                    placeholder="부재 시 경비실에 맡겨주세요."
                  />
                </Field>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5 space-y-3">
                <div className="font-serif text-lg text-primary">
                  결제 수단
                </div>
                <div className="flex items-center gap-3 p-3 border border-primary/30 rounded-sm bg-primary/5">
                  <div className="w-8 h-8 bg-[#0064FF] rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">T</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-primary">토스페이먼츠</div>
                    <div className="text-xs text-muted-foreground">
                      신용카드 · 체크카드 · 계좌이체 · 간편결제
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  결제하기 버튼을 누르면 토스페이먼츠 결제창이 열립니다.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="order-1 lg:order-2 lg:col-span-5">
            <Card className="sticky top-20 overflow-hidden py-0">
              <ArtworkPreview imageId={params.imageId} />
              <CardContent className="p-5 space-y-3">
                <div className="font-serif text-lg text-primary">
                  주문 요약
                </div>
                <div className="text-xs text-muted-foreground">
                  주문자: {user?.email ?? user?.name}
                </div>
                {estimate.data ? (
                  <div className="space-y-1 text-sm">
                    {estimate.data.products.map((p) => (
                      <div
                        key={p.id}
                        className="flex justify-between text-muted-foreground"
                      >
                        <span>{p.name}</span>
                        <span>{formatKRW(p.price)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between pt-3 border-t border-border/60">
                      <span className="text-muted-foreground">소계</span>
                      <span>{formatKRW(estimate.data.breakdown.subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">배송비</span>
                      <span>
                        {estimate.data.breakdown.shipping === 0
                          ? "무료"
                          : formatKRW(estimate.data.breakdown.shipping)}
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline pt-3 border-t border-border/60 text-base">
                      <span className="font-medium text-primary">결제 금액</span>
                      <span className="font-serif text-2xl tabular-nums text-primary">
                        {formatKRW(estimate.data.breakdown.total)}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">불러오는 중...</div>
                )}
                <Button
                  size="lg"
                  className="w-full mt-3 text-base font-semibold"
                  disabled={isPending}
                  onClick={submit}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      결제 처리 중...
                    </>
                  ) : (
                    "결제하기"
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </SitePageShell>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="mb-1.5 block text-xs text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function ArtworkPreview({ imageId }: { imageId: number }) {
  const sessionId = useMemo(
    () => window.localStorage.getItem("ops-last-session") ?? "",
    [],
  );
  const list = trpc.images.listSession.useQuery(
    { sessionId },
    { enabled: Boolean(sessionId) && imageId > 0 },
  );
  const img = list.data?.find((i) => i.id === imageId);
  if (!img) return null;
  return (
    <img
      src={img.url}
      alt="작품 미리보기"
      className="w-full aspect-[4/3] object-cover"
    />
  );
}
